import React, { useEffect, useRef, useState } from 'react'

// Custom cursor reticle. Snappy inner dot + smooth-following crosshair.
// Hidden on touch devices. Mounts once in App.js — appears across every route.

const FOLLOW = 0.18 // crosshair lag factor per frame (0 = no follow, 1 = no lag)

export default function CursorReticle() {
  const dotRef = useRef(null)
  const crossRef = useRef(null)
  // Live mouse position (mutated synchronously from mousemove for snappy dot).
  const mouseRef = useRef({ x: -100, y: -100 })
  // Smoothed position (interpolated each RAF frame for the crosshair).
  const smoothRef = useRef({ x: -100, y: -100 })
  const rafRef = useRef(0)
  const [hovering, setHovering] = useState(false)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Touch / coarse-pointer devices: skip entirely.
    if (typeof window === 'undefined') return undefined
    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches
    if (coarse) return undefined

    const onMove = (e) => {
      mouseRef.current.x = e.clientX
      mouseRef.current.y = e.clientY
      if (!visible) setVisible(true)
      // Snap the dot directly — no follow lag.
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${e.clientX - 2}px, ${e.clientY - 2}px, 0)`
      }
    }

    const onOver = (e) => {
      const t = e.target
      if (t && typeof t.closest === 'function') {
        const hit = t.closest('a, button, [data-clickable], .holding-row, .app-row, .v-chip, .plaque-claimed')
        setHovering(!!hit)
      }
    }

    const onLeave = () => setVisible(false)
    const onEnter = () => setVisible(true)

    const tick = () => {
      const m = mouseRef.current
      const s = smoothRef.current
      s.x += (m.x - s.x) * FOLLOW
      s.y += (m.y - s.y) * FOLLOW
      if (crossRef.current) {
        crossRef.current.style.transform = `translate3d(${s.x}px, ${s.y}px, 0) translate(-50%, -50%)`
      }
      rafRef.current = requestAnimationFrame(tick)
    }

    window.addEventListener('mousemove', onMove, { passive: true })
    window.addEventListener('mouseover', onOver, { passive: true })
    document.addEventListener('mouseleave', onLeave)
    document.addEventListener('mouseenter', onEnter)
    rafRef.current = requestAnimationFrame(tick)

    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseover', onOver)
      document.removeEventListener('mouseleave', onLeave)
      document.removeEventListener('mouseenter', onEnter)
      cancelAnimationFrame(rafRef.current)
    }
  }, [visible])

  return (
    <>
      <style>{`
        @media (pointer: fine) { body, a, button { cursor: none !important; } }
        @media (pointer: coarse) { .reticle-cross, .reticle-dot { display: none !important; } }
        .reticle-cross {
          position: fixed;
          left: 0; top: 0;
          width: 24px; height: 24px;
          pointer-events: none;
          z-index: 9999;
          mix-blend-mode: difference;
          transition: width 0.2s ease, height 0.2s ease, opacity 0.15s ease, filter 0.2s ease;
          will-change: transform, width, height;
        }
        .reticle-cross::before, .reticle-cross::after {
          content: '';
          position: absolute;
          background: #ff0055;
          box-shadow: 0 0 8px rgba(255,0,80,0.6);
        }
        .reticle-cross::before {
          left: 50%; top: 0;
          width: 1px; height: 100%;
          transform: translateX(-50%);
        }
        .reticle-cross::after {
          top: 50%; left: 0;
          height: 1px; width: 100%;
          transform: translateY(-50%);
        }
        .reticle-cross.is-hovering {
          width: 40px; height: 40px;
          filter: drop-shadow(0 0 6px rgba(255,0,80,0.9));
        }
        .reticle-dot {
          position: fixed;
          left: 0; top: 0;
          width: 4px; height: 4px;
          background: #ff0055;
          border-radius: 50%;
          pointer-events: none;
          z-index: 10000;
          box-shadow: 0 0 6px rgba(255,0,80,0.8);
          mix-blend-mode: difference;
          will-change: transform;
        }
      `}</style>
      <div
        ref={crossRef}
        className={`reticle-cross${hovering ? ' is-hovering' : ''}`}
        style={{ opacity: visible ? 1 : 0 }}
        aria-hidden="true"
      />
      <div
        ref={dotRef}
        className="reticle-dot"
        style={{ opacity: visible ? 1 : 0 }}
        aria-hidden="true"
      />
    </>
  )
}
